import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Search, X, LayoutDashboard, History, ShoppingCart, Factory, ShieldCheck, Truck, CheckCircle2, Inbox, ArrowRight, Clock, ShieldAlert, Flag, CalendarClock, SlidersHorizontal, Moon, AlignJustify, AtSign, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { inr } from "@/lib/ecotile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── shared search well ───────────────────────────────────────────────────────
// Metallic parity: a carved well with the rl-search-glass gradient disc on the
// right (same treatment as the ERP header search) — replaces the emoji 🔍 boxes
// that the Register and Stores views used to carry.
export function EcoSearchBox({ value, onChange, placeholder, className = "", width = "w-44" }) {
  return (
    <div className={`flex items-center gap-2 h-9 pl-3 pr-1 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/60 rl-carved ${className}`}>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
             className={`bg-transparent outline-none text-[12px] ${width}`} />
      <span className="rl-search-glass w-7 h-7 rounded-full flex items-center justify-center text-white shrink-0" aria-hidden="true">
        <Search className="w-3.5 h-3.5" />
      </span>
    </div>
  );
}

// ── tiny header→page event bus ───────────────────────────────────────────────
// EcotileLayout (the shell header) and EcotilePage (the data view) are separate
// components with no shared React state, so header actions reach the page via a
// window CustomEvent. ecoBus.emit({type, ...}) → EcotilePage listens on "eco:nav".
export const ecoBus = {
  emit: (detail) => { try { window.dispatchEvent(new CustomEvent("eco:nav", { detail })); } catch { /* ignore */ } },
  on: (fn) => { const h = (e) => fn(e.detail); window.addEventListener("eco:nav", h); return () => window.removeEventListener("eco:nav", h); },
};

const STAGE_META = {
  "PO Received": { color: "#3b82f6", Icon: Inbox },
  "Procurement": { color: "#f97316", Icon: ShoppingCart },
  "Manufacturing": { color: "#a855f7", Icon: Factory },
  "Quality Control": { color: "#14b8a6", Icon: ShieldCheck },
  "Dispatch": { color: "#f59e0b", Icon: Truck },
  "Completed": { color: "#22c55e", Icon: CheckCircle2 },
};
const GOTO = [
  { label: "Dashboard", view: "dashboard", Icon: LayoutDashboard, color: "#B78E10", hint: "H" },
  { label: "PO Received", view: "PO Received", Icon: Inbox, color: "#3b82f6", hint: "R" },
  { label: "Procurement", view: "Procurement", Icon: ShoppingCart, color: "#f97316", hint: "P" },
  { label: "Manufacturing", view: "Manufacturing", Icon: Factory, color: "#a855f7", hint: "M" },
  { label: "Quality Control", view: "Quality Control", Icon: ShieldCheck, color: "#14b8a6", hint: "Q" },
  { label: "Dispatch", view: "Dispatch", Icon: Truck, color: "#f59e0b", hint: "D" },
  { label: "Completed", view: "Completed", Icon: CheckCircle2, color: "#22c55e", hint: "C" },
  { label: "Register", view: "register", Icon: History, color: "#6e5600", hint: "G" },
  { label: "History log", view: "history", Icon: Clock, color: "#6e5600", hint: "L" },
];
// single-letter jumps (fire only when the query is empty) — like RockLink's palette
const SHORTCUTS = { h: "dashboard", r: "PO Received", p: "Procurement", m: "Manufacturing", q: "Quality Control", d: "Dispatch", c: "Completed", g: "register", l: "history" };

// ── magic search — ⌘K command palette over Ecotile orders + views ─────────────
export function EcotileCommandPalette({ onClose, viewControls }) {
  const [q, setQ] = useState("");
  const [orders, setOrders] = useState([]);
  const [idx, setIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const vc = viewControls || {};
  const isDark = vc.themePref === "dark" || (typeof document !== "undefined" && document.documentElement.classList.contains("dark") && vc.themePref !== "light");

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    let live = true;
    axios.get(`${API}/ecotile/orders`, { params: { fy: "2026-27" } }).then((r) => { if (live) setOrders(r.data.orders || []); }).catch(() => {});
    return () => { live = false; };
  }, []);

  const ql = q.trim().toLowerCase();

  // command rows — ATTENTION · FILTERS · VIEW · GO TO (mirrors the metallic palette)
  const commands = useMemo(() => [
    { section: "Attention", kind: "cmd", label: "Show overdue payments", Icon: Clock, color: "#ea580c", hint: "O", run: () => ecoBus.emit({ type: "registerFilter", filter: "overdue" }) },
    { section: "Attention", kind: "cmd", label: "No deadline set", Icon: CalendarClock, color: "#b45309", hint: "K", run: () => ecoBus.emit({ type: "registerFilter", filter: "nodl" }) },
    { section: "Attention", kind: "cmd", label: "Follow-up flagged", Icon: Flag, color: "#8A6FB8", hint: "F", run: () => ecoBus.emit({ type: "registerFilter", filter: "fu" }) },
    { section: "Attention", kind: "cmd", label: "Dispatch pending", Icon: Truck, color: "#f59e0b", hint: "Y", run: () => ecoBus.emit({ type: "registerFilter", filter: "dispp" }) },
    { section: "Filters", kind: "cmd", label: "Clear all filters", Icon: SlidersHorizontal, color: "#f97316", hint: "X", run: () => ecoBus.emit({ type: "registerFilter", filter: "all" }) },
    { section: "View", kind: "toggle", label: "Night mode", Icon: Moon, color: "#64748b", hint: "N", state: isDark ? "On" : "Off", run: () => vc.setThemePref && vc.setThemePref(isDark ? "light" : "dark") },
    { section: "View", kind: "toggle", label: "Reduce visual effects", Icon: AlignJustify, color: "#64748b", hint: "Z", state: vc.flatMode ? "On" : "Off", run: () => vc.toggleFlat && vc.toggleFlat() },
    ...GOTO.map((g) => ({ section: "Go to", kind: "goto", label: g.label, Icon: g.Icon, color: g.color, hint: g.hint, view: g.view })),
  ], [isDark, vc]);

  const cmdRows = useMemo(() => !ql ? commands : commands.filter((c) => c.label.toLowerCase().includes(ql)), [commands, ql]);
  const hits = useMemo(() => {
    if (!ql) return [];
    return orders.filter((o) =>
      (o.organisation_name || "").toLowerCase().includes(ql) ||
      (o.po_number || "").toLowerCase().includes(ql) ||
      (o.line_items || []).some((li) => (li.item_code || "").toLowerCase().includes(ql))
    ).slice(0, 40).map((o) => ({ section: "Orders", kind: "order", o }));
  }, [orders, ql]);

  const rows = useMemo(() => [...cmdRows, ...hits], [cmdRows, hits]);
  const sections = useMemo(() => {
    const out = []; let i = 0;
    for (const r of rows) {
      if (!out.length || out[out.length - 1].title !== r.section) out.push({ title: r.section, rows: [] });
      out[out.length - 1].rows.push({ r, i: i++ });
    }
    return out;
  }, [rows]);

  useEffect(() => { setIdx(0); }, [ql]);
  useEffect(() => { listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: "nearest" }); }, [idx]);

  const run = useCallback((row) => {
    if (!row) return;
    if (row.kind === "toggle") { row.run(); return; }               // toggles keep the palette open
    if (row.kind === "goto") ecoBus.emit({ type: "view", view: row.view });
    else if (row.kind === "cmd") row.run();
    else ecoBus.emit({ type: "openInStage", id: row.o.id, stage: row.o.stage || "PO Received", q: row.o.po_number || row.o.organisation_name });
    onClose();
  }, [onClose]);

  const onKey = (e) => {
    // NOTE: no single-letter shortcuts here — inside the search box every key must
    // type normally (letter shortcuts live on the page handler in EcotileLayout,
    // guarded against text fields). Only navigation keys are special.
    if (e.key === "/" && !ql) { e.preventDefault(); onClose(); return; }  // "/" closes when empty (it opened it)
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(i + 1, rows.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); run(rows[idx]); }
    else if (e.key === "Escape") { e.preventDefault(); onClose(); }
  };

  // Metallic behaviour: expand from the TOP, NO screen dim — a transparent
  // click-catcher closes it; the panel slides down from the header.
  return createPortal(
    <>
      <div className="fixed inset-0 z-[90]" onClick={onClose} data-testid="eco-cmdk-backdrop" />
      <div className="fixed left-1/2 -translate-x-1/2 top-2.5 z-[100] w-[min(600px,94vw)]" data-testid="eco-cmdk">
        {/* gradient-border input pill (rockpecker orange) */}
        <div className="rounded-xl p-[1.5px] shadow-lg animate-in fade-in slide-in-from-top-1 duration-150" style={{ background: "linear-gradient(135deg,#FFCC00 0%,#f97316 100%)" }}>
          <div className="h-10 flex items-center gap-2.5 px-3 rounded-[10px] bg-[hsl(var(--card))]">
            <Search className="w-4 h-4 shrink-0 text-[#f97316]" />
            <input ref={inputRef} value={q} onChange={(e) => { setQ(e.target.value); setIdx(0); }} onKeyDown={onKey}
              placeholder="Search items, navigate, filter…"
              className="flex-1 bg-transparent outline-none text-[14px] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/60 min-w-0"
              data-testid="eco-cmdk-input" autoComplete="off" spellCheck={false} />
            <kbd className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] shrink-0">Esc</kbd>
            <button onClick={onClose} className="p-0.5 rounded hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]/60 hover:text-[hsl(var(--foreground))] shrink-0" aria-label="Close"><X className="w-3.5 h-3.5" /></button>
          </div>
        </div>
        {/* dropdown — solid card, expands from the top */}
        <div ref={listRef} className="mt-1.5 max-h-[64vh] overflow-auto rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-xl animate-in fade-in slide-in-from-top-1 duration-150 py-1.5">
          {rows.length === 0 ? (
            <div className="px-4 py-8 text-center text-[13px] text-[hsl(var(--muted-foreground))]">No matches for “{q}”.</div>
          ) : sections.map((sec) => (
            <div key={sec.title}>
              <div className="px-4 pt-1.5 pb-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[hsl(var(--muted-foreground))]/60">{sec.title}</div>
              {sec.rows.map(({ r, i }) => {
                const active = i === idx; const Ic = r.Icon || (STAGE_META[r.o?.stage || "PO Received"] || {}).Icon || Search;
                const color = r.color || (STAGE_META[r.o?.stage || "PO Received"] || {}).color || "#94a3b8";
                return (
                  <button key={r.kind + (r.view || r.label || r.o?.id) + i} data-active={active} onMouseEnter={() => setIdx(i)} onClick={() => run(r)}
                    className="w-full flex items-center gap-3 px-4 py-2 text-left transition-colors" style={active ? { background: "hsl(var(--muted) / 0.7)" } : undefined}>
                    <Ic className="w-4 h-4 shrink-0" style={{ color }} />
                    {r.kind === "order" ? (
                      <>
                        <span className="flex-1 min-w-0">
                          <span className="block text-[13px] font-medium truncate">{r.o.organisation_name}</span>
                          <span className="block text-[11px] text-[hsl(var(--muted-foreground))] truncate font-mono-data">{r.o.po_number || "no PO"} · {r.o.stage || "PO Received"}</span>
                        </span>
                        <span className="text-[12px] font-mono-data tabular-nums text-[hsl(var(--muted-foreground))] shrink-0">{r.o.po_value_basic == null ? "" : inr(r.o.po_value_basic)}</span>
                      </>
                    ) : (
                      <span className="flex-1 text-[13.5px]">{r.label}</span>
                    )}
                    {r.kind === "toggle" && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] shrink-0">{r.state}</span>}
                    {r.hint && <kbd className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-[hsl(var(--muted))]/70 text-[hsl(var(--muted-foreground))]/80 shrink-0">{r.hint}</kbd>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </>,
    document.body
  );
}

// ── mentionable users (shared, module-cached) ────────────────────────────────
// The @-autocomplete in note inputs and the inbox both need the roster. Fetch
// it once per session and hand out the cached copy; failures degrade to [].
let _ecoMentionCache = null;
let _ecoMentionPromise = null;
export function useMentionables() {
  const [users, setUsers] = useState(_ecoMentionCache || []);
  useEffect(() => {
    if (_ecoMentionCache) { setUsers(_ecoMentionCache); return; }
    if (!_ecoMentionPromise) {
      _ecoMentionPromise = axios.get(`${API}/users/mentionables`)
        .then((r) => { _ecoMentionCache = Array.isArray(r.data) ? r.data : []; return _ecoMentionCache; })
        .catch(() => { _ecoMentionPromise = null; return []; });
    }
    let alive = true;
    _ecoMentionPromise.then((list) => { if (alive) setUsers(list || []); });
    return () => { alive = false; };
  }, []);
  return users;
}

// ── @-mention text input ─────────────────────────────────────────────────────
// A drop-in replacement for a plain note <input>: type "@" then a name and pick
// a teammate from the dropdown to tag them. Selecting inserts "@Full Name " so
// the backend's mention parser (matches @Name up to 3 words) resolves it. The
// menu triggers on a single-word token after "@"; picking a name completes it.
export function EcoMentionInput({ value, onChange, onEnter, placeholder, className, disabled }) {
  const users = useMentionables();
  const ref = useRef(null);
  const [menu, setMenu] = useState({ open: false, items: [], active: 0, atPos: 0 });

  const closeMenu = useCallback(() => setMenu((m) => (m.open ? { ...m, open: false } : m)), []);

  const recompute = useCallback((val, caret) => {
    const before = val.slice(0, caret);
    const mm = /(?:^|\s)@([^\s@]*)$/.exec(before);
    if (!mm) { closeMenu(); return; }
    const q = mm[1].toLowerCase();
    const atPos = caret - mm[1].length - 1;
    const items = (users || [])
      .filter((u) => {
        if (!q) return true;
        const name = (u.name || "").toLowerCase();
        const local = (u.email || "").split("@")[0].toLowerCase();
        return name.includes(q) || local.includes(q);
      })
      .slice(0, 6);
    if (!items.length) { closeMenu(); return; }
    setMenu({ open: true, items, active: 0, atPos });
  }, [users, closeMenu]);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    const caret = e.target.selectionStart ?? val.length;
    recompute(val, caret);
  };

  const pick = (u) => {
    const label = (u.name && u.name.trim()) || (u.email || "").split("@")[0];
    const caret = ref.current?.selectionStart ?? value.length;
    const next = value.slice(0, menu.atPos) + "@" + label + " " + value.slice(caret);
    onChange(next);
    closeMenu();
    // restore caret just after the inserted mention
    const pos = menu.atPos + label.length + 2;
    requestAnimationFrame(() => {
      try { ref.current?.focus(); ref.current?.setSelectionRange(pos, pos); } catch { /* noop */ }
    });
  };

  const onKeyDown = (e) => {
    if (menu.open) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMenu((m) => ({ ...m, active: (m.active + 1) % m.items.length })); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setMenu((m) => ({ ...m, active: (m.active - 1 + m.items.length) % m.items.length })); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(menu.items[menu.active]); return; }
      if (e.key === "Escape") { e.preventDefault(); closeMenu(); return; }
    }
    if (e.key === "Enter") { e.preventDefault(); onEnter?.(); }
  };

  return (
    <div className="relative flex-1">
      <input
        ref={ref}
        value={value}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(closeMenu, 120)}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        data-testid="eco-mention-input"
      />
      {menu.open && (
        <div className="absolute left-0 top-full mt-1 z-[80] w-[240px] rl-frost rl-hero-glass rounded-xl border border-[hsl(var(--border))] shadow-xl overflow-hidden py-1" data-testid="eco-mention-menu">
          {menu.items.map((u, i) => (
            <button
              key={u.id || u.email || i}
              type="button"
              // onMouseDown (not onClick) so it fires before the input's onBlur closes the menu
              onMouseDown={(e) => { e.preventDefault(); pick(u); }}
              className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left transition-colors ${i === menu.active ? "bg-[#FFCC00]/20" : "hover:bg-[hsl(var(--muted))]/50"}`}
              data-testid={`eco-mention-opt-${i}`}
            >
              <span className="flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold shrink-0" style={{ background: "linear-gradient(135deg,#FFCC00,#E6B800)", color: "#2b2500" }}>
                {((u.name || u.email || "?").trim()[0] || "?").toUpperCase()}
              </span>
              <span className="min-w-0">
                <span className="block text-[12.5px] font-medium text-[hsl(var(--foreground))] truncate">{u.name || (u.email || "").split("@")[0]}</span>
                {u.role && <span className="block text-[10px] text-[hsl(var(--muted-foreground))] truncate">{u.role}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── mention inbox bell (Ecotile) ─────────────────────────────────────────────
function ecoRelative(iso) {
  if (!iso) return "";
  try {
    const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" });
  } catch { return ""; }
}

function EcoStageChip({ stage }) {
  if (!stage) return null;
  const color = STAGE_META[stage]?.color || "hsl(var(--muted-foreground))";
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-px rounded-full leading-none whitespace-nowrap" style={{ backgroundColor: color + "1f", color }}>
      <span className="w-[5px] h-[5px] rounded-full shrink-0" style={{ backgroundColor: color }} />
      {stage}
    </span>
  );
}

export function EcotileMentionInbox() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ unread: [], read: [], unread_count: 0 });
  const firstPoll = useRef(true);
  const shown = useRef(new Set());

  const fetchMentions = useCallback(async (opts = {}) => {
    try {
      const res = await axios.get(`${API}/ecotile/mentions`);
      const d = res.data || { unread: [], read: [], unread_count: 0 };
      setData(d);
      if (!firstPoll.current && opts.announce !== false) {
        for (const m of d.unread || []) {
          if (!shown.current.has(m.id)) {
            shown.current.add(m.id);
            toast(`${m.mentioned_by} mentioned you · ${m.current_po_number || m.po_number || ""}`.trim(), {
              description: (m.text_snippet || "").slice(0, 100),
            });
          }
        }
      } else {
        for (const m of d.unread || []) shown.current.add(m.id);
      }
      firstPoll.current = false;
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    fetchMentions({ announce: false });
    const id = setInterval(() => fetchMentions(), 60000);
    return () => clearInterval(id);
  }, [fetchMentions]);

  const markAllRead = useCallback(async () => {
    try {
      await axios.post(`${API}/ecotile/mentions/read-all`);
      setData((prev) => ({
        unread: [],
        read: [...prev.unread.map((x) => ({ ...x, read: true })), ...prev.read].slice(0, 20),
        unread_count: 0,
      }));
    } catch { /* noop */ }
  }, []);

  const goTo = (m) => {
    if (!m.order_exists || !m.current_stage) return;
    if (!m.read) {
      axios.post(`${API}/ecotile/mentions/${m.id}/read`).catch(() => {});
      setData((prev) => {
        const moved = (prev.unread || []).find((x) => x.id === m.id);
        return {
          unread: (prev.unread || []).filter((x) => x.id !== m.id),
          read: moved ? [{ ...moved, read: true }, ...(prev.read || [])].slice(0, 20) : (prev.read || []),
          unread_count: Math.max(0, (prev.unread_count || 0) - 1),
        };
      });
    }
    setOpen(false);
    ecoBus.emit({ type: "openInStage", id: m.order_id, stage: m.current_stage, q: m.current_po_number || m.po_number || "" });
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) fetchMentions({ announce: false });
    if (!next && (data.unread_count || 0) > 0) markAllRead();
  };

  const count = data.unread_count || 0;
  const all = [...(data.unread || []), ...(data.read || [])];

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`relative inline-flex items-center justify-center w-8 h-8 rounded-full transition-[colors,box-shadow] duration-150 ${
            open
              ? "bg-zinc-200 dark:bg-zinc-800 text-[hsl(var(--muted-foreground))] [box-shadow:inset_0_1px_3px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.06)]"
              : count > 0 ? "text-[#c8992a] rl-frost-btn" : "text-[hsl(var(--muted-foreground))] rl-frost-btn hover:text-[hsl(var(--foreground))]"
          }`}
          title={count > 0 ? `${count} new mention${count === 1 ? "" : "s"}` : "Mentions"}
          data-testid="eco-mention-inbox-trigger"
        >
          <AtSign className="w-3.5 h-3.5" strokeWidth={2.2} />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold leading-none shadow-sm">
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="z-50 p-0 w-[360px] max-h-[70vh] overflow-hidden rounded-xl rl-frost rl-hero-glass border-[hsl(var(--border))] flex flex-col" data-testid="eco-mention-inbox-panel">
        <div className="px-3 pt-3 pb-2.5 flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-2">
            <AtSign className="w-4 h-4 shrink-0 text-[#c8992a]" />
            <span className="text-sm font-semibold text-[hsl(var(--foreground))]">Mentions</span>
          </span>
          {count > 0 && (
            <button type="button" onClick={markAllRead} className="text-[11px] font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors" data-testid="eco-mention-read-all">
              Mark all read
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {all.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 px-6 text-center text-[hsl(var(--muted-foreground))]">
              <Inbox className="w-8 h-8 opacity-30 mb-2" strokeWidth={1.2} />
              <p className="text-sm">No mentions yet</p>
              <p className="text-[11px] mt-0.5">You'll see pings here when teammates tag you in an order note.</p>
            </div>
          ) : (
            all.map((m) => {
              const navigable = !!(m.order_exists && m.current_stage);
              return (
                <div
                  key={m.id}
                  role={navigable ? "button" : undefined}
                  tabIndex={navigable ? 0 : undefined}
                  onClick={navigable ? () => goTo(m) : undefined}
                  onKeyDown={navigable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goTo(m); } } : undefined}
                  className={`px-3 py-2.5 border-b border-[hsl(var(--border))]/40 transition-colors ${m.read ? "opacity-70" : "bg-amber-500/5"} ${navigable ? "group cursor-pointer hover:bg-[hsl(var(--muted))]/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#E6B800]" : ""}`}
                  title={navigable ? `Open in ${m.current_stage} and find this order` : "This order was removed"}
                  data-testid={`eco-mention-item-${m.id}`}
                >
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                    <span className="inline-flex items-center gap-1">
                      <span className="font-semibold normal-case text-[hsl(var(--foreground))]">{m.mentioned_by}</span>
                      {m.mentioned_by_role && <span className="normal-case">· {m.mentioned_by_role}</span>}
                    </span>
                    <span className="ml-auto tabular-nums">{ecoRelative(m.created_at)}</span>
                    {navigable && (
                      <ArrowRight className="w-3.5 h-3.5 shrink-0 text-[#c8992a] opacity-0 -translate-x-1 transition-all duration-200 ease-out group-hover:opacity-100 group-hover:translate-x-0" strokeWidth={2.4} aria-hidden="true" />
                    )}
                  </div>
                  <p className="mt-1 text-[12.5px] text-[hsl(var(--foreground))] line-clamp-2 leading-snug">{m.text_snippet}</p>
                  <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                    {m.current_stage && <EcoStageChip stage={m.current_stage} />}
                    {(m.organisation_name && m.organisation_name !== "—") && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-[hsl(var(--muted-foreground))]">
                        <Building2 className="w-3 h-3 shrink-0" />{m.organisation_name}
                      </span>
                    )}
                    {(m.current_po_number || m.po_number) && (
                      <span className="font-mono-data text-[10px] bg-[hsl(var(--muted))]/40 px-1 rounded text-[hsl(var(--foreground))]">{m.current_po_number || m.po_number}</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
